package com.kinoshazam.screens.rootFlow

import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavGraphBuilder
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.dialog
import androidx.navigation.compose.rememberNavController
import analytics.Analytics
import analytics.ScreenTracker
import com.kinoshazam.Navigator
import com.kinoshazam.changeLanguage
import com.kinoshazam.deeplink.DeeplinkHandler
import com.kinoshazam.history.BindBrowserHistory
import com.kinoshazam.model.AppLanguage
import com.kinoshazam.model.Route
import com.kinoshazam.model.ThemeState
import com.kinoshazam.screens.allVideo.host.AllVideosContentHost
import com.kinoshazam.screens.appInfo.compact.AppInfoContentHost
import com.kinoshazam.screens.decide.DecideContentHost
import com.kinoshazam.screens.details.host.VideoDetailsContentHost
import com.kinoshazam.screens.login.compact.CompactLoginContentHost
import com.kinoshazam.screens.login.compact.CompactOtpScreenHost
import com.kinoshazam.screens.main.host.MainWindowHost
import com.kinoshazam.screens.password.compact.CompactChangePasswordContentHost
import com.kinoshazam.screens.password.compact.CompactForgotPasswordContentHost
import com.kinoshazam.screens.person.host.PersonContentHost
import com.kinoshazam.screens.profile.host.ProfileContentHost
import com.kinoshazam.screens.search.host.SearchContentHost
import com.kinoshazam.uiKit.config.uiKitFormFactor
import com.kinoshazam.uiKit.theme.uiKitLocalizationState
import com.kinoshazam.uiKit.theme.uiKitThemeState
import com.kinoshazam.view.OnBackPressed
import com.kinoshazam.view.ProvideFormFactor
import com.kinoshazam.screens.history.HistoryContentHost
import com.kinoshazam.view.WebPlayerHost
import com.kinoshazam.view.rememberExitApp
import org.koin.compose.viewmodel.koinViewModel
import org.koin.mp.KoinPlatform.getKoin
import presentation.screens.RootViewModel
import utils.FormFactor

@Composable
fun RootContent(
    modifier: Modifier = Modifier,
    isTv: Boolean = false,
    deepLink: String? = null,
) {

    val navController = rememberNavController()
    val modalNavController = rememberNavController()
    val navigator: Navigator = getKoin().get()
    DisposableEffect(navController) {
        navigator.bind(navController, modalNavController)
        onDispose { navigator.unBind() }
    }

    val rootViewModel = koinViewModel<RootViewModel>()

    val state = rootViewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state.value.local) {
        state.value.local?.let {
            val local = AppLanguage.fromCode(it)
            changeLanguage(AppLanguage.fromCode(it))
            uiKitLocalizationState.value = local
        }
    }

    LaunchedEffect(state.value.theme) {
        state.value.theme?.let {
            val theme = ThemeState.fromName(it)
            uiKitThemeState.value = theme
        }
    }

    LaunchedEffect(state.value.features, state.value.appUpdate) {
//        GlobalConfig.appUpdate = state.value.appUpdate
        // TODO: update global data
    }
//    val stack by component.stack.subscribeAsState()
//    val state by component.state.subscribeAsState()
    // form-factor info
    // https://developer.android.com/develop/ui/compose/layouts/adaptive?hl=ru
    // https://developer.android.com/guide/topics/large-screens?hl=ru
    // https://developer.android.com/guide/topics/large-screens/large-screen-cookbook?hl=ru


//    BoxWithConstraints(
//        modifier = Modifier.size(40.dp, 40.dp).background(Color.Red)
//    ) {
//        when {
////            isAndroidTv -> ExpandedMainWindow() делать отдельную сборку с зашитым флагом в gradle
//            maxWidth.value >= 600 -> MediumWindow()
//            maxWidth.value >= 840 -> MediumWindow() //ExpandedWindow()
//            else -> CompactWindow(component, modifier)
//        }
//    }

    // TODO: do form factor logic
//    val navController = rememberNavController()
//    val navigator = remember(navController) {
//        getKoin().get<Navigator> { parametersOf(navController) }
//    }

    val forceUpdateRequired = state.value.appUpdate?.let { it.isNeedUpdate && it.forceUpdate } == true
    val exitApp = rememberExitApp()

    // Force update: keep the update dialog on screen until the user installs or exits.
    LaunchedEffect(forceUpdateRequired) {
        if (forceUpdateRequired) {
            navigator.navigateTo(Route.Update)
        }
    }

    // Do not key()/remount NavHost on locale change: on web stringResource is async and
    // remount leaves menu titles stuck as "". Locale is applied via __customLocale +
    // LocalLocalization (compositionLocalOf) + appStringResource() subscribers.
    ProvideFormFactor(modifier = modifier, isAndroidTv = isTv) { formFactor ->

            LaunchedEffect(formFactor) {
                uiKitFormFactor.value = formFactor.value
            }

            NavHost(
                navController = navController,
                startDestination = Route.Main
            ) {
                buildMainNavGraph(
                    formFactor = formFactor,
                    // TV pushes dialogs on the main controller; phone/desktop use the modal host.
                    includeDialogs = isTv,
                )

                dialog<Route.Update>(
                    dialogProperties = DialogProperties(
                        // Don't pop the dialog on back during force update — we exit the app instead.
                        dismissOnBackPress = !forceUpdateRequired,
                        dismissOnClickOutside = false,
                    )
                ) {
                    // Dialog owns the back dispatcher; handle exit here so back isn't swallowed.
                    OnBackPressed(enabled = forceUpdateRequired) {
                        exitApp()
                    }
                    UpdateScreen(
                        state = state.value,
                        cancel = {
                            if (forceUpdateRequired) {
                                exitApp()
                            } else {
                                navigator.back()
                            }
                        },
                        onAction = {
                            rootViewModel.onAction(it)
                        }
                    )
                }
            }

            // Between main and modal NavHosts so priority is:
            // Main drawer/menu < nav back < modal bottom-sheet handlers.
            val mainEntry by navController.currentBackStackEntryAsState()
            val modalEntry by modalNavController.currentBackStackEntryAsState()
            val canGoBack = remember(mainEntry, modalEntry) { navigator.canGoBack() }
            val analytics: Analytics = remember { getKoin().get() }
            val screenTracker = remember(analytics) { ScreenTracker(analytics) }

            LaunchedEffect(mainEntry, modalEntry) {
                val screen = analyticsScreen(modalEntry, mainEntry) ?: return@LaunchedEffect
                screenTracker.track(screen.name, screen.properties)
            }

            OnBackPressed(enabled = canGoBack && !forceUpdateRequired) {
                navigator.back()
            }

            NavHost(
                navController = modalNavController,
                startDestination = "empty"
            ) {
                composable("empty") { }

                if (!isTv) {
                    buildDialogDestinations(formFactor = formFactor)
                }
            }

            DeeplinkHandler(deepLink, navigator::navigateTo)
            // After the join handler: its effect consumes `/join/` before this one
            // writes the address. Don't reorder them.
            BindBrowserHistory(navController, modalNavController)
    }
}

/** Stack destinations (main window). */
private fun NavGraphBuilder.buildMainNavGraph(
    formFactor: State<FormFactor>,
    includeDialogs: Boolean,
) {
    composable<Route.Main> {
        MainWindowHost(formFactor = formFactor.value)
    }

    composable<Route.AllVideo> {
        AllVideosContentHost(formFactor = formFactor.value)
    }

    composable<Route.AppInfo> {
        AppInfoContentHost()
    }

    composable<Route.History> {
        HistoryContentHost(formFactor = formFactor.value)
    }

    if (includeDialogs) {
        buildDialogDestinations(formFactor)
    }
}

private fun NavGraphBuilder.buildDialogDestinations(formFactor: State<FormFactor>) {
    composable<Route.Dialog.Details>(
        // No fade layer: the banner hole has to erase pixels on the root canvas, and a
        // transition layer would keep the poster area opaque. Exit is also instant so the
        // trailer view does not stay after the details content is gone.
        enterTransition = { EnterTransition.None },
        exitTransition = { ExitTransition.None },
        popEnterTransition = { EnterTransition.None },
        popExitTransition = { ExitTransition.None },
    ) { entry ->
        VideoDetailsContentHost(
            formFactor = formFactor.value,
            backStackEntry = entry,
        )
    }

    composable<Route.Dialog.WebPlayer> {
        WebPlayerHost()
    }

    composable<Route.Dialog.Search> {
        SearchContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.Login> {
        CompactLoginContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.OtpScreen> {
        CompactOtpScreenHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.Profile> {
        ProfileContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.ChangePassword> {
        CompactChangePasswordContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.ForgotPassword> {
        CompactForgotPasswordContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.Decide> {
        DecideContentHost(formFactor = formFactor.value)
    }

    composable<Route.Dialog.Person> { entry ->
        PersonContentHost(
            formFactor = formFactor.value,
            backStackEntry = entry,
        )
    }
}
